// The project you are working in, held by nav v2's shell (components/shell/Shell.tsx) for every screen under it.
// The model is lib/projectScope.ts; this file reads the projects, remembers the choice and hands screens a filter.
//
// ONLY THE SHELL PROVIDES IT. The classic sidebar has no switcher to change a project with, so a screen outside the
// shell (?nav=classic, the dev harnesses) gets the default here: All sites, nothing narrowed, nothing read.
//
// THE READS. GET admin/projects names your projects; which websites a project holds comes from GET admin/project
// (one read per project, the same cache entry Settings › Workspace › Projects › <project> uses), or straight from the
// list once it carries `site_ids` (xano/project-workspaces). Only what the choice needs is read: the chosen project;
// every project only for "Not in a project", or once the switcher has been opened (its counts).
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQueries, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useScope } from "./scope";
import { useWebsites } from "./websites";
import type { WebsiteList } from "@wire";
import {
  PROJECT_KEY, filterWebsites, formatProjectChoice, membershipFromList, parseProjectChoice, pinnedWorkspace, projectFilter, projectsToRead, settleChoice,
  type Membership, type ProjectChoice, type ProjectFilter,
} from "./projectScope";
import { getProject, listProjects, projectKey, projectsKey, type ProjectListItem } from "./projects";

export interface ProjectScopeValue {
  choice: ProjectChoice;
  setChoice: (next: ProjectChoice) => void;
  /** Your projects, by name. Empty while loading, when you have none, or when they could not be read. */
  projects: ProjectListItem[];
  /** The chosen project, when the choice is one. */
  current?: ProjectListItem;
  loading: boolean;
  /** The project list could not be read: the switcher says so, and nothing is narrowed. */
  failed: boolean;
  /** null: nothing narrows (All sites, or a membership that could not be read). undefined: still being read. */
  filter: ProjectFilter | null | undefined;
  membership: Membership;
  /** A project's websites could not be read, so every website is shown rather than none. */
  membershipFailed: boolean;
  /** Read every project's websites (the switcher's counts). */
  wantAll: () => void;
}

const DEFAULT: ProjectScopeValue = {
  choice: null, setChoice: () => {}, projects: [], loading: false, failed: false, filter: null, membership: new Map(), membershipFailed: false, wantAll: () => {},
};
const Ctx = createContext<ProjectScopeValue | null>(null);

function stored(): ProjectChoice {
  try {
    return parseProjectChoice(localStorage.getItem(PROJECT_KEY)) ?? null;
  } catch {
    return null;
  }
}

export function ProjectScopeProvider({ children }: { children: React.ReactNode }) {
  const { setScope } = useScope();
  const [choice, setChoiceState] = useState<ProjectChoice>(stored);
  const [all, setAll] = useState(false);

  const listQ = useQuery({ queryKey: projectsKey, queryFn: ({ signal }) => listProjects(signal), staleTime: 5 * 60_000, retry: false });
  const projects = useMemo(() => listQ.data?.projects ?? [], [listQ.data]);

  const remember = useCallback((next: ProjectChoice) => {
    setChoiceState(next);
    try {
      if (next === null) localStorage.removeItem(PROJECT_KEY);
      else localStorage.setItem(PROJECT_KEY, formatProjectChoice(next));
    } catch {
      /* private mode: the choice lasts for this visit */
    }
  }, []);
  const setChoice = useCallback((next: ProjectChoice) => {
    remember(next);
    // A project that pins a data workspace takes it along; one that doesn't leaves the data workspace alone.
    const pin = pinnedWorkspace(next, projects);
    if (pin) setScope(pin);
  }, [remember, projects, setScope]);

  // Another tab changed it.
  useEffect(() => {
    const changed = (e: StorageEvent) => { if (e.key === PROJECT_KEY) setChoiceState(parseProjectChoice(e.newValue) ?? null); };
    window.addEventListener("storage", changed);
    return () => window.removeEventListener("storage", changed);
  }, []);
  // A remembered project that is no longer yours falls back to All sites, once the list has actually loaded.
  useEffect(() => {
    if (!listQ.isSuccess) return;
    const settled = settleChoice(choice, projects);
    if (settled !== choice) remember(settled);
  }, [listQ.isSuccess, projects, choice, remember]);

  const toRead = useMemo(() => projectsToRead(choice, projects, all), [choice, projects, all]);
  const details = useQueries({
    queries: toRead.map((id) => ({ queryKey: projectKey(id), queryFn: ({ signal }: { signal: AbortSignal }) => getProject(id, signal), staleTime: 5 * 60_000, retry: false })),
  });
  const detailSig = details.map((d) => `${d.status}:${d.dataUpdatedAt}`).join(",");
  const membership = useMemo(() => {
    const m = membershipFromList(projects);
    toRead.forEach((id, i) => { const d = details[i]?.data; if (d) m.set(id, (d.sites ?? []).map((s) => s.site_id)); });
    return m as Membership;
  }, [projects, toRead, detailSig]); // eslint-disable-line react-hooks/exhaustive-deps
  const membershipFailed = details.some((d) => d.isError);

  const filter = useMemo(() => {
    if (choice === null) return null;
    if (listQ.isError) return null;
    if (listQ.isPending) return undefined;
    // Only the reads this choice needs: a failed count read for the switcher must not unscope a project that loaded.
    const f = projectFilter(choice, projects, membership);
    if (f === undefined && membershipFailed) return null;
    return f;
  }, [choice, listQ.isError, listQ.isPending, projects, membership, membershipFailed]);

  const wantAll = useCallback(() => setAll(true), []);
  const value = useMemo<ProjectScopeValue>(() => ({
    choice, setChoice, projects, current: typeof choice === "number" ? projects.find((p) => p.id === choice) : undefined,
    loading: listQ.isPending, failed: listQ.isError, filter, membership, membershipFailed, wantAll,
  }), [choice, setChoice, projects, listQ.isPending, listQ.isError, filter, membership, membershipFailed, wantAll]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The project scope, or All sites outside nav v2's shell. */
export function useProjectScope(): ProjectScopeValue {
  return useContext(Ctx) ?? DEFAULT;
}

/**
 * GET /v1/websites narrowed to the chosen project: the hook a screen that lists "all sites" reads instead of
 * useWebsites. While the project's websites are still being read it is pending (a list that waits, rather than one
 * that shows everything and then shrinks). One website by its address still comes from useWebsites / findSite: an
 * address may name any website you can open.
 */
export function useScopedWebsites(): UseQueryResult<WebsiteList> {
  const q = useWebsites();
  const { filter } = useProjectScope();
  const data = useMemo(() => (!q.data || filter === null ? q.data : filter === undefined ? undefined : { ...q.data, websites: filterWebsites(q.data.websites, filter) }), [q.data, filter]);
  if (filter === null) return q;
  const waiting = filter === undefined && !q.isError;
  return { ...q, data, isPending: q.isPending || waiting, isLoading: q.isLoading || waiting, isSuccess: q.isSuccess && !waiting } as UseQueryResult<WebsiteList>;
}
